'use client'

import { Card, CardBody, CardFooter, CardHeader } from '@heroui/card'
import { Skeleton } from '@heroui/skeleton'
import { format } from 'date-fns'
import { useRouter } from 'next/navigation'
import { useEffect } from 'react'
import { useState } from 'react'

import { Typography } from '@/components/ui'
import { ImageList } from '@/components/widgets'
import { useLocalImagesByMomentId } from '@/data'
import { useDecrypt } from '@/hooks'
import { routes } from '@/routes'
import { truncateText } from '@/utils'

import { StarFilledIcon } from '../icons'

export interface CardMomentProps {
  id: string
  images: {
    original: string
    order: number
    type: string
    thumbnail: string
  }[]
  title: string
  content: string
  date: string
  isFavorite: boolean
}

const CardMoment = ({
  id,
  images,
  title,
  content,
  date,
  isFavorite,
}: CardMomentProps) => {
  const router = useRouter()
  const formattedDate = format(date, 'dd MMM, yyyy')
  // Add state to store blob URLs
  const [blobUrls, setBlobUrls] = useState<string[]>([])

  const { data: localImages = [] } = useLocalImagesByMomentId(id)

  const { data: decryptedContent = '', isLoading: isLoadingContent } =
    useDecrypt({
      ciphertext: content,
    })
  const { data: decryptedTitle = '', isLoading: isLoadingTitle } = useDecrypt({
    ciphertext: title,
  })

  const handleOpenDetail = () => {
    router.push(`${routes.detailMoment(id)}`)
  }

  // Create blob URLs when local images are loaded
  useEffect(() => {
    if (localImages.length > 0) {
      const urls = localImages.map((image) => {
        const blob = new Blob([image.media], { type: image.type })
        return URL.createObjectURL(blob)
      })
      setBlobUrls(urls)

      // Cleanup function to revoke blob URLs
      return () => {
        urls.forEach((url) => URL.revokeObjectURL(url))
      }
    }
  }, [localImages])

  const showSkeleton = isLoadingContent || isLoadingTitle

  return (
    <>
      <Card
        isPressable
        fullWidth
        onPress={handleOpenDetail}
        shadow="none"
        classNames={{
          base: 'flex flex-col gap-1 pl-4 pr-6 pt-4 pb-5 border border-primary-100 bg-primary-50',
          header: 'p-0',
          footer: 'p-0 rounded-none',
          body: 'p-0',
        }}
      >
        <CardHeader>
          <div className="flex w-full flex-col items-start gap-3">
            {localImages.length > 0 && (
              <ImageList withPreview images={blobUrls} />
            )}
            {!localImages.length && images?.length > 0 && (
              <ImageList
                withPreview
                encryptedImages={{
                  original:
                    images.map((image) => ({
                      path: image.original,
                      type: image.type,
                    })) ?? [],
                  thumbnail:
                    images.map((image) => ({
                      path: image.thumbnail,
                      type: image.type,
                    })) ?? [],
                }}
              />
            )}
            {showSkeleton ? (
              <Skeleton className="h-3 w-1/2 rounded-sm" />
            ) : (
              <Typography
                size="xsmall"
                weight="medium"
                className="text-start font-crimsonPro text-lg leading-[22px]"
              >
                {decryptedTitle}
              </Typography>
            )}
          </div>
        </CardHeader>
        <CardBody>
          {showSkeleton ? (
            <div className="flex flex-col gap-1">
              <Skeleton className="h-3 w-full rounded-sm" />
              <Skeleton className="h-3 w-full rounded-sm" />
            </div>
          ) : (
            <Typography
              size="small"
              weight="light"
              className="font-crimsonPro leading-[18px]"
            >
              {truncateText(decryptedContent, 160)}
            </Typography>
          )}
        </CardBody>
        <CardFooter>
          <div className="mt-2 flex w-full items-center justify-between">
            <Typography size="small" weight="light" className="font-crimsonPro">
              {formattedDate}
            </Typography>
            {isFavorite && (
              <StarFilledIcon className="text-base text-primary-400" />
            )}
          </div>
        </CardFooter>
      </Card>
    </>
  )
}

export default CardMoment
